import {
  FC,
  Fragment,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  selectBuilderPublicPermissions,
  UrlBuilderPublicState,
} from "store/url-builder-public";
import { SectionValues } from "utils/sections";

import { findPermission } from "utils/permissions";
import { ObjectPermissionSetData } from "types/permissions";
import { useSelector } from "react-redux";
import { CustomCTA } from "store/url-builder";
import { getVideoUrl } from "utils/pathBuilder";
import { GetConfigData } from "store/config/selectors";
import { Divider, Grid, Typography } from "@material-ui/core";
import { ContactMeForm } from "components/shared/contact-me-form";
import { PortfolioCard } from "store/card";

//sections
const About = lazy(() => import("./About"));
const SocialNetworks = lazy(() => import("./SocailNetworks"));
const Video = lazy(() => import("./Video"));
const Gallery = lazy(() => import("./Gallery"));
const Testimonials = lazy(() => import("./Testimonials"));
const Cta = lazy(() => import("./Cta"));
const AppStores = lazy(() => import("./AppStores"));
const PaymentOptions = lazy(() => import("./PaymentOptions"));

const Sections: FC<{
  portfolioType: string;
  portfolio: UrlBuilderPublicState;
  portCard: PortfolioCard;
  userAgent: string;
}> = ({ portfolioType, portfolio, portCard, userAgent }) => {
  const [sections, setSections] = useState(SectionValues);

  const confData = useSelector(GetConfigData);
  const portfolioPermissions = useSelector(selectBuilderPublicPermissions);
  const mainVideoPath = getVideoUrl(portfolio.video, confData);

  const [ctaList, setCtaList] = useState<CustomCTA[]>([]);

  const permission = useCallback(
    (filter: { moduleId: number; permissionName: string }) =>
      findPermission(portfolioPermissions as ObjectPermissionSetData[], filter)
        ?.objectPermissionValue,
    [portfolioPermissions]
  );

  //re-order sections
  useEffect(() => {
    const order = portfolio.order || [];
    const sortedSections = sections
      .slice(0)
      .sort((a, b) => order.indexOf(a) - order.indexOf(b));

    setSections(sortedSections);
  }, [portfolio.order]);

  //extract cta data
  useEffect(() => {
    const buttonTexts = portfolio.customCTA.buttonText
      ? portfolio.customCTA.buttonText.split("#lcard;")
      : [];
    const buttonLinks = portfolio.customCTA.buttonLink
      ? portfolio.customCTA.buttonLink.split("#lcard;")
      : [];
    const buttonColor = portfolio.customCTA.buttonColor
      ? portfolio.customCTA.buttonColor.split("#lcard;")
      : buttonTexts.map((item) => portfolio.primaryColor.color);

    let temp: CustomCTA[] = [];

    buttonTexts.forEach((item, index) => {
      if (item && buttonLinks[index])
        temp.push({
          buttonText: item,
          buttonLink: buttonLinks[index],
          buttonColor: buttonColor[index],
        });
    });

    setCtaList(temp);
  }, [portfolio.customCTA, portfolio.primaryColor.color]);

  return (
    <Fragment>
      {sections.map(function (section, index) {
        if (
          section === "about" &&
          (portfolioType === "preview" ||
            permission({
              moduleId: 9,
              permissionName: "visible",
            }))
        )
          return (
            <Suspense fallback={() => null} key={index}>
              {portfolio.aboutMe.text && <About portfolio={portfolio} />}
            </Suspense>
          );
        else if (
          section === "social" &&
          (portfolioType === "preview" ||
            permission({
              moduleId: 10,
              permissionName: "visible",
            }))
        )
          return (
            <Suspense fallback={() => null} key={index}>
              {portfolio.socialNetworks.length > 0 && (
                <SocialNetworks portfolio={portfolio} />
              )}
            </Suspense>
          );
        else if (
          section === "video" &&
          (portfolioType === "preview" ||
            permission({
              moduleId: 11,
              permissionName: "visible",
            }))
        )
          return (
            <Suspense fallback={() => null} key={index}>
              {mainVideoPath && <Video mainVideoPath={mainVideoPath} />}
            </Suspense>
          );
        else if (
          section === "gallery" &&
          (portfolioType === "preview" ||
            permission({
              moduleId: 14,
              permissionName: "visible",
            }))
        )
          return (
            <Suspense fallback={() => null} key={index}>
              {portfolio.images && <Gallery portfolio={portfolio} />}
            </Suspense>
          );
        else if (
          section === "testimonials" &&
          (portfolioType === "preview" ||
            permission({
              moduleId: 17,
              permissionName: "visible",
            }))
        )
          return (
            <Suspense fallback={() => null} key={index}>
              {portfolio.testimonials.length > 0 && (
                <Testimonials portfolio={portfolio} />
              )}
            </Suspense>
          );
        else if (
          section === "cta" &&
          (portfolioType === "preview" ||
            permission({
              moduleId: 23,
              permissionName: "visible",
            }))
        )
          return (
            <Suspense fallback={() => null} key={index}>
              {ctaList.length > 0 && (
                <Cta portfolio={portfolio} ctaList={ctaList} />
              )}
            </Suspense>
          );
        else if (
          section === "apps" &&
          (portfolioType === "preview" ||
            permission({
              moduleId: 24,
              permissionName: "visible",
            }))
        )
          return (
            <Suspense fallback={() => null} key={index}>
              {portfolio.appStores.length > 0 && (
                <AppStores portfolio={portfolio} />
              )}
            </Suspense>
          );
        else if (
          section === "payment" &&
          (portfolioType === "preview" ||
            permission({
              moduleId: 25,
              permissionName: "visible",
            }))
        )
          return (
            <Suspense fallback={() => null} key={index}>
              {portfolio.paymentOptions.length > 0 && (
                <PaymentOptions portfolio={portfolio} />
              )}
            </Suspense>
          );
        else if (
          section === "reachout" &&
          (portfolioType === "preview" ||
            permission({
              moduleId: 26,
              permissionName: "visible",
            }))
        )
          return (
            <Fragment key={index}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Reach Out
                </Typography>
                <ContactMeForm
                  color={portfolio.primaryColor.color}
                  fileds={portfolio.contactForm.fields}
                  userId={"" + portCard.userId}
                  userAgent={userAgent}
                  cardId={portCard.id}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider light />
              </Grid>
            </Fragment>
          );
      })}
    </Fragment>
  );
};

export default Sections;
